<!-- The first-run walkthrough. Factory-owned.

     Every app this factory has built shipped without one, because it was always
     something the app "should" add and never the thing anyone got to. So the
     chassis ships the whole mechanism — the panes, the progress, the skip, the
     seen-it flag — and an app supplies nothing but the cards.

     WHY THIS WAS REBUILT. The first version was stock Ionic furniture: the
     card's title in an `IonToolbar`, a bare `<img>` at the top of a padded
     `IonContent`, grey body text, and an `IonFooter` reading "Back · 1 of 3 ·
     Next". Every piece of it was a correct Ionic component and the result
     looked like a settings wizard, because that is what those components are
     for. Onboarding is the first screen of the app and the only one whose whole
     job is to make a promise, and it was the ugliest screen in every app the
     factory made. Specifically:

       · the most important line on the screen rendered at toolbar size, in a
         bar, while the content area began with a stray image
       · the image had no dimensions, so it stretched or sat small at the left
       · content was top-aligned in a scroll box, leaving half a phone empty
       · progress was the string "1 of 3" in a footer title
       · Skip sat top-right where it reads as Done, competing with Next
       · cards swapped instantly, so there was no sense of moving through them

     WHAT IT IS NOW. No toolbar and no footer: a centred column with the
     illustration in a fixed-aspect box that cannot stretch, the title as a
     display heading in the content where it belongs, the body under it, dots
     for progress, ONE full-width primary action, and Skip as a quiet text link
     below it. Cards crossfade with direction, and collapse to a plain fade
     under `prefers-reduced-motion`.

     STILL NO BESPOKE CHROME. `IonModal` presents itself correctly on both
     platforms and that has not changed; what is bespoke here is the CONTENT
     layout, which is exactly the part Ionic has no opinion about. Swipe stays
     gone: Ionic 7 dropped IonSlides for Swiper, and a carousel is not worth a
     dependency in a starter. The buttons do the same job and are reachable by
     keyboard and screen reader, which a swipe never was.

     Renders NOTHING when there are no cards. A half-built onboarding showing
     one blank panel is worse than none, so the empty case is the safe one. -->
<template>
  <IonModal :is-open="open" :backdrop-dismiss="false" class="onboarding">
    <IonContent :scroll-y="false">
      <div class="ob">
        <!-- The art. A FIXED ASPECT BOX, because an <img> with a width and no
             height stretches to whatever the file happens to be, and the file
             is supplied by the app. `contain` and `object-position: center` mean
             a square icon and a wide illustration both land correctly. -->
        <!-- A real capture of the screen the card is about, framed like a
             phone (`shot: true`, which is what the plan-generated cards set),
             or a plain illustration. If the image is missing or fails to load
             — a fresh build before its first capture — the card's icon takes
             its place on an accent disc, so a card is never a blank box. -->
        <div class="ob__art" aria-hidden="true">
          <figure v-if="card?.img && card.shot && !broken[card.img]" :key="card.img" class="ob__shot" data-motion>
            <img :src="card.img" alt="" @error="broken[card.img] = true">
          </figure>
          <img
            v-else-if="card?.img && !broken[card.img]"
            :key="`img-${card.img}`"
            :src="card.img"
            alt=""
            data-motion
            @error="broken[card.img] = true"
          >
          <div v-else-if="card?.icon" :key="`icon-${index}`" class="ob__glyph" data-motion>
            <IonIcon :icon="card.icon" aria-hidden="true" />
          </div>
        </div>

        <!-- The promise. `:key` on the block so Vue replaces rather than
             patches it, which is what gives the crossfade something to animate
             and what makes a screen reader re-announce the new card. -->
        <div :key="index" class="ob__say" data-motion>
          <h1 class="ob__title">{{ card?.title }}</h1>
          <p v-if="card?.body" class="ob__body">{{ card.body }}</p>
        </div>

        <!-- Dots, not "1 of 3". The count is not the information; where you are
             in it is. Hidden from the accessibility tree because the live
             region below says it in words. -->
        <div class="ob__dots" aria-hidden="true">
          <span
            v-for="(c, i) in cards"
            :key="i"
            class="ob__dot"
            :class="{ 'ob__dot--on': i === index }"
          />
        </div>
        <p class="sr-only" aria-live="polite">Step {{ index + 1 }} of {{ cards.length }}</p>

        <!-- ONE primary action, full width, in the thumb zone. On the last card
             it says what happens next rather than "Done" — the button IS the
             first real action of the app. -->
        <div class="ob__actions">
          <IonButton expand="block" size="large" @click="next">
            {{ index >= cards.length - 1 ? finishLabel : 'Next' }}
          </IonButton>

          <!-- Quiet, below, and never on the last card — there is nothing left
               to skip, and two buttons doing the same thing is a choice nobody
               wants to make. Back is here too: secondary actions live together,
               under the primary one. -->
          <div class="ob__minor">
            <IonButton v-if="index > 0" fill="clear" size="small" @click="index -= 1">
              Back
            </IonButton>
            <IonButton
              v-if="index < cards.length - 1"
              fill="clear"
              size="small"
              @click="finish"
            >
              Skip
            </IonButton>
          </div>
        </div>
      </div>
    </IonContent>
  </IonModal>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { IonModal, IonContent, IonButton, IonIcon } from '@ionic/vue';
import { prefs } from '../storage.js';

const props = defineProps({
  /**
   * [{ title, body?, img?, shot?, icon? }] — THREE, or four at the very outside.
   *
   * `img` with `shot: true` is a capture of one of the app's own screens and
   * is framed like a phone; `icon` (an ionicons SVG) stands in whenever the
   * image is absent or fails to load. A plan-built app gets all of this from
   * `src/onboarding.js`, which `appfactory product apply` writes from the plan.
   *
   * Each card makes ONE promise, in the customer's words, about something the
   * app does for them. Not a feature tour and not a manual: if a card explains
   * how a control works, the control is the thing to fix. The last card's
   * button is the first real action of the app, so name it — "Add your first
   * item", not "Get started".
   */
  cards: { type: Array, default: () => [] },
  /** What the last card's button says. Name the action, not the ceremony. */
  finishLabel: { type: String, default: 'Get started' },
  /** Bump when the cards change materially and everyone should see them again. */
  version: { type: [String, Number], default: 1 },
});

const index = ref(0);
// Images that failed to load, so the card falls back to its icon instead of
// showing a broken-image glyph in the middle of the first screen of the app.
const broken = reactive({});
const seen = ref(true);
const key = computed(() => `onboarding.v${props.version}`);
const open = computed(() => props.cards.length > 0 && !seen.value);
const card = computed(() => props.cards[index.value] ?? null);

// `prefs` is a Proxy over localStorage — property access, not a method call.
onMounted(() => { seen.value = prefs[key.value] === true; });

function finish() {
  seen.value = true;
  prefs[key.value] = true;
}

function next() {
  if (index.value >= props.cards.length - 1) finish();
  else index.value += 1;
}
</script>
